import React, { useState } from "react";
import PropTypes from "prop-types";
import BaseButton from "components/common/BaseButton";
import CircleCloseFilterSvg from "components/svg/CircleCloseFilter";
import { withRouter } from "react-router-dom";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { setIsSaving } from "redux/actions/isSavingActions";
import { getCaseDataRepositoryWithoutOffline } from "core/caseRepository/caseRepositoryFactory";
import DuplicateCaseModal from "./DuplicateCaseModal";
import ReactModal from "react-modal";

const TableCellOverlay = ({ caseId, history, status, rowPrefix }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.currentUser);

  const [duplicatedCaseId, setDuplicatedCaseId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  ReactModal.setAppElement("#app");

  
  const closeOverlay = event => {
    if (event !== undefined) {
      event.stopPropagation();
    }

    const row = document.querySelector(`[data-row-id="${!rowPrefix ? caseId : (rowPrefix + caseId)}"]`);
    row.classList.remove("table__body-row--is-active");
  };

  const clearOverlays = () => {
    const overlays = document.querySelectorAll(".table__body-row--is-active");

    overlays.forEach(row => {
      row.classList.remove("table__body-row--is-active");
    });
  };

  const formatURL = (url, clonedCaseId) => {
    if (!url || !clonedCaseId) return url;
    const originParam = encodeURIComponent(
      window.location.pathname + window.location.search
    );

    let formattedUrl = url;

    const regexPatterns = [
      { pattern: /\/my-reports(\?.*)?$/, replacement: `/case/${clonedCaseId}?from=${originParam}`},
      { pattern: /\/all-reports(\?.*)?$/,replacement: `/case/${clonedCaseId}?from=${originParam}`},
      { pattern: /\/dashboard(\?.*)?$/, replacement: `/case/${clonedCaseId}?from=${originParam}`},
      { pattern: /\/reports(\?.*)?$/, replacement: `/case/${clonedCaseId}?from=${originParam}`},
      { pattern: /\/case\/[^/?#]+([?#].*)?$/, replacement: `/case/${clonedCaseId}`},
    ];

    regexPatterns.forEach(({ pattern, replacement }) => {
      if (url.match(pattern)) {
        formattedUrl = url.replace(pattern, replacement);
      }
    });

    return formattedUrl;
  }

  const duplicateCase = async (event, id) => {
    event.stopPropagation();

    const repository = getCaseDataRepositoryWithoutOffline();
    try {
      dispatch(setIsSaving(true));
      const clonedCaseResponse = await repository.cloneCase(id);
      const clonedId = clonedCaseResponse.data.id;

      if (currentUser) {
        const caseData = await repository.getCaseData(clonedId);
        const existingAnswers = caseData.data ?? [];

        // Detect template type from existing answers — avoids an extra API call
        const isUSInspection = existingAnswers.some(
          a => a.name && a.name.startsWith('reportDetails.inspectionMadeBy')
        );

        const authorFields = isUSInspection
          ? [
              { name: "reportDetails.inspectionMadeBy.name",  value: JSON.stringify(currentUser.name ?? "") },
              { name: "reportDetails.inspectionMadeBy.email", value: JSON.stringify(currentUser.email ?? "") },
              { name: "reportDetails.inspectionMadeBy.phone", value: JSON.stringify(currentUser.phone_number ?? "") },
            ]
          : [
              { name: "skfDetails.reportAuthor.name",  value: JSON.stringify(currentUser.name ?? "") },
              { name: "skfDetails.reportAuthor.email", value: JSON.stringify(currentUser.email ?? "") },
              { name: "skfDetails.reportAuthor.phone", value: JSON.stringify(currentUser.phone_number ?? "") },
            ];

        await Promise.all(authorFields.map(field => {
          const existing = existingAnswers.find(a => a.name === field.name);
          if (existing) {
            return repository.updateAnswer(clonedId, existing.id, { name: field.name, value: field.value });
          }
          return repository.addAnswer(clonedId, { name: field.name, value: field.value });
        }));
      }

      setDuplicatedCaseId(clonedId);
      clearOverlays();
    } catch (e) {
      console.log('There was an error duplicating case ' + id, e);
      alert('There was an error duplicating case:\n' + e);
    } finally {
      dispatch(setIsSaving(false));
    }
  };

  const redirectToCase = event => {
    event.stopPropagation();

    history.push({
      pathname: `/case/${caseId}`,
      state: {
        from: {
          pathname: history.location.pathname,
          search: history.location.search,
        },
      },
    });
  };

  return (
    <div className="table__body-overlay-element">
      {showModal && duplicatedCaseId && (
        <DuplicateCaseModal
          duplicatedCaseId={duplicatedCaseId}
          setShowModal={setShowModal}
          showModal={showModal}
          formatURL={formatURL}
        />
      )}
      <BaseButton
        text={intl.formatMessage({
          id: "tableCellOverlay.openReport"
        })}
        backgroundColor="green"
        handleSubmit={event => {
          redirectToCase(event);
        }}
      />

      <BaseButton
        text={intl.formatMessage({
          id: "tableCellOverlay.duplicate"
        })}
        backgroundColor="gray"
        handleSubmit={(e) => {
          setShowModal(true);
          duplicateCase(e, caseId);
        }}
      />

      <BaseButton
        className="table__body-overlay-element-close"
        iconComponent={<CircleCloseFilterSvg />}
        handleSubmit={event => closeOverlay(event)}
      />
    </div>
  );
};

TableCellOverlay.propTypes = {
  caseId: PropTypes.string,
  status: PropTypes.string
};

export default withRouter(TableCellOverlay);